import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, ChevronDown, ChevronUp, X, Image as ImageIcon, Upload, GripVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { api, formatApiErrorDetail, uploadImage, resolveImg, normalizeExternalUrl } from "../../lib/api";
import ImageCropperModal from "../../components/ImageCropperModal";
import { DndContext, closestCorners, PointerSensor, useSensor, useSensors, useDroppable, DragOverlay, MeasuringStrategy } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const EMPTY_ITEM = {
  section_id: "",
  name: "",
  brand: "",
  description: "",
  image_url: "",
  url: "",
  highlight: "",
  price_or_meta: "",
  cta_label: "Learn more",
  meta_extras: [],
  category: "",
  active: true,
};

// ---------------------------------------------------------------- ITEM EDITOR
function ItemEditor({ open, item, sectionTitle, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [form, setForm] = useState(EMPTY_ITEM);
  const [metaInput, setMetaInput] = useState("");
  // Advanced fields (brand, highlight, price, category, chips) are collapsed by
  // default to keep the "add item" flow uncluttered. Auto-open the section only
  // when editing an existing item that already uses any advanced field, so we
  // never hide data an admin has previously set.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      const next = { ...EMPTY_ITEM, ...(item || {}) };
      setForm(next);
      setMetaInput("");
      const hasAdvanced = !!(next.brand || next.highlight || next.price_or_meta || next.category || (next.meta_extras && next.meta_extras.length));
      setShowAdvanced(hasAdvanced);
    }
  }, [open, item]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const addMeta = () => {
    const v = metaInput.trim();
    if (!v) return;
    set("meta_extras", [...(form.meta_extras || []), v]);
    setMetaInput("");
  };
  const removeMeta = (i) => set("meta_extras", (form.meta_extras || []).filter((_, idx) => idx !== i));

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCropSrc(URL.createObjectURL(f));
    e.target.value = "";
  };
  const editExisting = async () => {
    if (!form.image_url) return;
    try {
      const r = await fetch(resolveImg(form.image_url), { credentials: "omit" });
      const blob = await r.blob();
      setCropSrc(URL.createObjectURL(blob));
    } catch { toast.error("Couldn't load image — upload a new one"); }
  };
  const onCropped = async (blob) => {
    setCropSrc(null);
    setUploading(true);
    try {
      const file = new File([blob], `crop-${Date.now()}.jpg`, { type: blob.type || "image/jpeg" });
      const url = await uploadImage(file);
      set("image_url", url);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, name: form.name.trim() };
      if (form.id) {
        const { data } = await api.put(`/admin/items/${form.id}`, payload);
        onSaved(data);
      } else {
        const { data } = await api.post("/admin/items", payload);
        onSaved(data);
      }
      toast.success(form.id ? "Item updated" : "Item added");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white text-black sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{form.id ? "Edit item" : "Add item"}{sectionTitle ? ` · ${sectionTitle}` : ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Image</Label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={editExisting}
                disabled={!form.image_url}
                title={form.image_url ? "Click to re-crop" : "No image"}
                className="group relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-black/10 bg-black/5 disabled:cursor-default"
              >
                {form.image_url ? (
                  <>
                    <img src={resolveImg(form.image_url)} alt="" className="h-full w-full object-cover" />
                    <span className="absolute inset-0 hidden items-center justify-center bg-black/50 text-[10px] font-bold text-white group-hover:flex">Re-crop</span>
                  </>
                ) : <ImageIcon size={18} className="text-black/30" />}
              </button>
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="ie-upload" className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-3.5 py-2 text-xs font-semibold">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {form.image_url ? "Replace" : "Upload"} & crop
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} />
            </div>
            <Input placeholder="or paste an image URL" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} data-testid="ie-image_url" />
          </div>
          <Fld label="Name (required)" value={form.name} onChange={(v) => set("name", v)} testid="ie-name" />
          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="ie-description" />
          </div>
          <Fld
            label="Link URL"
            value={form.url}
            onChange={(v) => set("url", v)}
            onBlur={() => {
              // Auto-fix bare domains so the admin sees the corrected URL
              // right after they tab out of the field. Backend does the same
              // on save — belt and braces.
              const fixed = normalizeExternalUrl(form.url);
              if (fixed !== form.url) set("url", fixed);
            }}
            placeholder="https://example.com  (or just example.com)"
            testid="ie-url"
          />
          <Fld label="CTA button label" value={form.cta_label} onChange={(v) => set("cta_label", v)} testid="ie-cta" />

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            data-testid="ie-toggle-advanced"
            className="flex w-full items-center justify-between rounded-lg border border-dashed border-black/15 px-3 py-2 text-[11px] font-semibold text-black/60 hover:bg-black/[0.03]"
          >
            <span>{showAdvanced ? "Hide advanced options" : "Show advanced options"}</span>
            <span className="text-[10px] text-black/40">Brand · Badge · Price · Category · Chips</span>
          </button>

          {showAdvanced && (
            <div className="space-y-3 rounded-lg border border-black/5 bg-black/[0.015] p-3">
              <TwoCol>
                <Fld label="Brand / attribution" value={form.brand} onChange={(v) => set("brand", v)} testid="ie-brand" />
                <Fld label="Category (sub-heading, optional)" value={form.category} onChange={(v) => set("category", v)} testid="ie-category" />
              </TwoCol>
              <TwoCol>
                <Fld label="Highlight badge (e.g. 10% OFF, LTF)" value={form.highlight} onChange={(v) => set("highlight", v)} testid="ie-highlight" />
                <Fld label="Price / meta line (e.g. ₹500, Free forever)" value={form.price_or_meta} onChange={(v) => set("price_or_meta", v)} testid="ie-price" />
              </TwoCol>
              <div className="space-y-1">
                <Label className="text-xs">Extra chips (small labels below CTA)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {(form.meta_extras || []).map((m, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2.5 py-1 text-[11px] font-semibold">
                      {m}
                      <button type="button" onClick={() => removeMeta(i)} data-testid={`ie-meta-remove-${i}`} className="rounded-full p-0.5 hover:bg-black/10"><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={metaInput} onChange={(e) => setMetaInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMeta(); } }} placeholder="e.g. Zero forex, Visa network" data-testid="ie-meta-input" />
                  <button type="button" onClick={addMeta} data-testid="ie-meta-add" className="rounded-lg border border-black/15 px-3 text-xs font-semibold">Add</button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-black/15 px-4 py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="ie-save" className="inline-flex items-center gap-2 rounded-full bg-black px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : null} {form.id ? "Save" : "Add"}
          </button>
        </div>
        {cropSrc && <ImageCropperModal src={cropSrc} defaultAspect={1} onCancel={() => setCropSrc(null)} onCropped={onCropped} />}
      </DialogContent>
    </Dialog>
  );
}
function Fld({ label, value, onChange, onBlur, placeholder, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}
function TwoCol({ children }) { return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>; }

// ---------------------------------------------------------------- ITEM ROW (sortable)
// Each row carries its `section_id` in useSortable data so the parent DndContext
// can distinguish same-section reorders from cross-section moves.
function SortableItemRow({ it, onEdit, onDelete, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: it.id,
    data: { type: "item", sectionId: it.section_id },
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`unified-item-${it.id}`} className="flex items-center gap-3 rounded-lg border border-black/10 bg-white p-2.5">
      <button {...attributes} {...listeners} className="cursor-grab rounded-md p-1 text-black/40 hover:bg-black/5 active:cursor-grabbing" aria-label="Drag" data-testid={`unified-item-handle-${it.id}`}>
        <GripVertical size={14} />
      </button>
      {it.image_url ? <img src={resolveImg(it.image_url)} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover" /> : <div className="h-10 w-10 shrink-0 rounded-md bg-black/5" />}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {it.brand ? <span className="text-[11px] font-bold uppercase tracking-wider text-black/50">{it.brand}</span> : null}
          {it.category ? <span className="rounded-full bg-black/5 px-1.5 py-0.5 text-[9px] font-semibold text-black/60">{it.category}</span> : null}
        </div>
        <p className="truncate text-[14px] font-semibold">{it.name}</p>
        {it.highlight ? <p className="truncate text-[11px] font-semibold" style={{ color: "#B84A26" }}>{it.highlight}</p> : null}
      </div>
      <button onClick={onToggle} data-testid={`unified-item-toggle-${it.id}`} className={`rounded-lg p-1.5 ${it.active !== false ? "text-emerald-600 hover:bg-emerald-50" : "text-black/30 hover:bg-black/5"}`} title={it.active !== false ? "Active" : "Hidden"}>
        {it.active !== false ? <Eye size={14} /> : <EyeOff size={14} />}
      </button>
      <button onClick={onEdit} className="rounded-lg p-1.5 text-black/60 hover:bg-black/5" data-testid={`unified-item-edit-${it.id}`}><Pencil size={14} /></button>
      <button onClick={onDelete} className="rounded-lg p-1.5 text-red-600 hover:bg-red-50" data-testid={`unified-item-delete-${it.id}`}><Trash2 size={14} /></button>
    </div>
  );
}

// A droppable zone the user can drop an item into. Always rendered — even for
// collapsed sections — so cross-section drops never trigger a layout reflow
// that would corrupt dnd-kit's cached active-draggable rect.
function SectionDropZone({ sectionId, empty, collapsed }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `dropzone-${sectionId}`,
    data: { type: "section", sectionId },
  });
  const minHeight = collapsed ? 40 : empty ? 60 : 12;
  return (
    <div
      ref={setNodeRef}
      data-testid={`unified-section-dropzone-${sectionId}`}
      className={`rounded-md border-2 border-dashed transition-colors ${
        isOver
          ? "border-[color:#B84A26] bg-[color:#B84A26]/5"
          : collapsed
          ? "border-black/10 bg-black/[0.02]"
          : empty
          ? "border-black/15 bg-black/[0.02]"
          : "border-transparent"
      }`}
      style={{ minHeight }}
    >
      {collapsed ? (
        <p className="py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-black/35">
          {isOver ? "Drop to move here" : "Drop items here to add to this section"}
        </p>
      ) : empty ? (
        <p className="py-4 text-center text-xs text-black/40">
          {isOver ? "Drop to move here" : 'No items yet. Click "Add item" or drop one from another section.'}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- SECTION ROW
function SectionRow({ tile, index, total, items, onMove, onEdit, onDelete, onAddItem, onEditItem, onDeleteItem, onToggleItem, expanded, setExpanded }) {
  return (
    <div className="rounded-2xl border border-[rgba(17,17,17,0.08)] bg-white p-4" data-testid={`unified-section-${tile.id}`}>
      <div className="mb-3 flex items-center gap-2">
        <span className="rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black/60">{String(index + 1).padStart(2, "0")}</span>
        {tile.type === "custom" ? <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fuchsia-700">Custom</span> : <span className="text-[10px] font-mono text-black/40">{tile.id}</span>}
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => onMove(index, -1)} disabled={index === 0} className="rounded-lg p-1.5 text-black/60 hover:bg-black/5 disabled:opacity-30"><ChevronUp size={15} /></button>
          <button onClick={() => onMove(index, 1)} disabled={index === total - 1} className="rounded-lg p-1.5 text-black/60 hover:bg-black/5 disabled:opacity-30"><ChevronDown size={15} /></button>
          <button onClick={() => onEdit({ ...tile, visible: !tile.visible })} className={`rounded-lg p-1.5 ${tile.visible ? "text-emerald-600 hover:bg-emerald-50" : "text-black/30 hover:bg-black/5"}`}>{tile.visible ? <Eye size={15} /> : <EyeOff size={15} />}</button>
          {tile.type === "custom" && <button onClick={onDelete} className="rounded-lg p-1.5 text-red-600 hover:bg-red-50" data-testid={`unified-section-delete-${tile.id}`}><Trash2 size={15} /></button>}
        </div>
      </div>
      <div className="space-y-2">
        <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={tile.title} onChange={(e) => onEdit({ ...tile, title: e.target.value })} data-testid={`unified-section-title-${tile.id}`} /></div>
        <div className="space-y-1"><Label className="text-xs">Subtitle</Label><Input value={tile.subtitle || ""} onChange={(e) => onEdit({ ...tile, subtitle: e.target.value })} data-testid={`unified-section-subtitle-${tile.id}`} /></div>
      </div>

      <div className="mt-4">
        <div className="flex w-full items-center gap-2 rounded-lg bg-black/[0.03] px-3 py-2 text-xs font-semibold">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            data-testid={`unified-section-toggle-${tile.id}`}
            className="flex flex-1 items-center gap-2 text-left"
          >
            <ChevronDown size={13} className={`transition-transform ${expanded ? "rotate-0" : "-rotate-90"}`} />
            <span>Items · {items.length}</span>
          </button>
          <button
            type="button"
            onClick={onAddItem}
            data-testid={`unified-add-item-${tile.id}`}
            className="flex items-center gap-1 text-[11px] font-semibold text-[color:#B84A26] hover:underline"
          >
            <Plus size={12} /> Add item
          </button>
        </div>
      </div>
      {expanded ? (
        <div className="mt-2 space-y-2 rounded-xl bg-black/[0.02] p-2">
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            {items.map((it) => (
              <SortableItemRow key={it.id} it={it} onEdit={() => onEditItem(it)} onDelete={() => onDeleteItem(it)} onToggle={() => onToggleItem(it)} />
            ))}
          </SortableContext>
          {/* Empty-state / append drop zone. Always rendered so cross-section
              drops have a target even when the section already has items. */}
          <SectionDropZone sectionId={tile.id} empty={items.length === 0} />
        </div>
      ) : (
        // COLLAPSED sections still expose a fixed-height drop strip so drags
        // from other sections can land here without any layout reflow. This
        // avoids the dnd-kit cached-rect bug that broke upward drags into
        // collapsed sections (iteration_17 / iteration_18 CRITICAL).
        <div className="mt-2">
          <SectionDropZone sectionId={tile.id} empty={false} collapsed />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- SECTION TEMPLATES
// Templates create a tile + optionally seed a few starter items so admins never
// stare at an empty section. Keep them intentionally light — just enough to show
// the shape of a good section for that category.
const SECTION_TEMPLATES = [
  {
    key: "blank",
    label: "Blank section",
    hint: "Empty section — add your own items",
    title: "New section",
    subtitle: "",
    items: [],
  },
  {
    key: "products",
    label: "Products",
    hint: "Discounted products, gear, gadgets",
    title: "Products I recommend",
    subtitle: "Personally used & tested — use my code for a discount",
    items: [
      { name: "Sample product", brand: "BRAND", description: "Why I like it in one line.", highlight: "10% OFF", price_or_meta: "₹999", cta_label: "Shop now" },
    ],
  },
  {
    key: "services",
    label: "Services",
    hint: "Bookable services & consultations",
    title: "Work with me",
    subtitle: "Book a slot or explore concierge services",
    items: [
      { name: "1:1 Consultation", brand: "CONSULT", description: "30 min personalised session over Zoom.", highlight: "POPULAR", price_or_meta: "₹1,499", cta_label: "Book a slot" },
    ],
  },
  {
    key: "freebies",
    label: "Freebies",
    hint: "Guides, newsletters, downloads",
    title: "Free resources",
    subtitle: "Guides, checklists, and tools I share for free",
    items: [
      { name: "Free guide", brand: "GUIDE", description: "One-line description of the freebie.", highlight: "FREE", price_or_meta: "PDF", cta_label: "Download" },
    ],
  },
];

// ---------------------------------------------------------------- ROOT
export default function UnifiedContent() {
  const [tiles, setTiles] = useState(null);
  const [savedTiles, setSavedTiles] = useState(null); // snapshot to detect unsaved edits
  const [items, setItems] = useState([]);
  const [savingTiles, setSavingTiles] = useState(false);
  const [editing, setEditing] = useState(null); // {open, item, section_id, sectionTitle}
  const [expanded, setExpanded] = useState({});
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const load = useCallback(async () => {
    const [content, itemsRes] = await Promise.all([
      api.get("/content"),
      api.get("/admin/items"),
    ]);
    const nextTiles = content.data.settings?.tiles || [];
    setTiles(nextTiles);
    setSavedTiles(JSON.parse(JSON.stringify(nextTiles)));
    setItems(itemsRes.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep-equal via JSON snapshot — tile edits are shallow so this is cheap+correct.
  const dirty = useMemo(() => {
    if (!tiles || !savedTiles) return false;
    return JSON.stringify(tiles) !== JSON.stringify(savedTiles);
  }, [tiles, savedTiles]);

  // Warn on tab close/refresh when there are unsaved section edits.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const itemsBySection = useMemo(() => {
    const map = {};
    for (const it of items) (map[it.section_id] ||= []).push(it);
    for (const k in map) map[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return map;
  }, [items]);

  const updateTile = (id, patch) => setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const moveTile = (i, dir) => setTiles((prev) => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const deleteTile = async (id) => {
    if (!window.confirm("Delete this section and all its items?")) return;
    // Persist the tiles change FIRST (atomic) so we never orphan items on reload.
    const nextTiles = tiles.filter((t) => t.id !== id);
    try {
      await api.put("/admin/settings", { tiles: nextTiles });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return;
    }
    setTiles(nextTiles);
    setSavedTiles(JSON.parse(JSON.stringify(nextTiles)));
    const doomed = items.filter((it) => it.section_id === id);
    for (const it of doomed) await api.delete(`/admin/items/${it.id}`);
    setItems((prev) => prev.filter((it) => it.section_id !== id));
    toast.success("Section deleted");
  };
  const addCustom = () => {
    const id = `custom_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    setTiles((prev) => [...prev, { id, type: "custom", title: "New section", subtitle: "", visible: true }]);
    setExpanded((p) => ({ ...p, [id]: true }));
    // Auto-scroll to the new section on the next paint so the admin sees it
    // immediately (they might already be scrolled past several existing sections).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-testid="unified-section-${id}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          // Brief highlight so the user's eye lands on the new section.
          el.classList.add("ring-2", "ring-[color:#B84A26]", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-[color:#B84A26]", "ring-offset-2"), 1600);
          // Focus the title input so they can start typing immediately.
          const titleInput = el.querySelector(`[data-testid="unified-section-title-${id}"]`);
          titleInput?.focus?.();
          titleInput?.select?.();
        }
      });
    });
    toast.success("Section added — start filling it in below");
  };

  const addFromTemplate = async (tmpl) => {
    setTemplatesOpen(false);
    // Guard: block templates if there are unsaved section edits so we don't
    // silently commit them along with the new tile.
    if (dirty) {
      toast.error("Save or discard your section edits first before adding a template.");
      return;
    }
    const id = `custom_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const newTile = { id, type: "custom", title: tmpl.title, subtitle: tmpl.subtitle, visible: true };
    const nextTiles = [...tiles, newTile];
    // Persist the tile + seeded items atomically so the admin can immediately see live items.
    try {
      await api.put("/admin/settings", { tiles: nextTiles });
      const createdItems = [];
      for (const seed of (tmpl.items || [])) {
        const payload = {
          section_id: id,
          name: seed.name || "Untitled",
          brand: seed.brand || "",
          description: seed.description || "",
          image_url: "",
          url: "",
          highlight: seed.highlight || "",
          price_or_meta: seed.price_or_meta || "",
          cta_label: seed.cta_label || "Learn more",
          meta_extras: [],
          category: "",
          active: true,
        };
        const { data } = await api.post("/admin/items", payload);
        createdItems.push(data);
      }
      setTiles(nextTiles);
      setSavedTiles(JSON.parse(JSON.stringify(nextTiles)));
      setItems((prev) => [...prev, ...createdItems]);
      setExpanded((p) => ({ ...p, [id]: true }));
      // Auto-scroll to the new template section (same UX as addCustom).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.querySelector(`[data-testid="unified-section-${id}"]`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            el.classList.add("ring-2", "ring-[color:#B84A26]", "ring-offset-2");
            setTimeout(() => el.classList.remove("ring-2", "ring-[color:#B84A26]", "ring-offset-2"), 1600);
          }
        });
      });
      toast.success(`Added "${tmpl.title}" from template`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveTiles = async () => {
    setSavingTiles(true);
    try {
      await api.put("/admin/settings", { tiles });
      const r = await api.get("/content");
      const nextTiles = r.data.settings?.tiles || [];
      setTiles(nextTiles);
      setSavedTiles(JSON.parse(JSON.stringify(nextTiles)));
      toast.success("Sections saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingTiles(false); }
  };

  const addItem = (sectionId, sectionTitle) => {
    // Auto-expand the section so the newly-created item is immediately visible.
    setExpanded((p) => ({ ...p, [sectionId]: true }));
    setEditing({ open: true, item: { section_id: sectionId }, sectionTitle });
  };
  const editItem = (it, sectionTitle) => setEditing({ open: true, item: it, sectionTitle });
  const toggleItem = async (it) => {
    const next = items.map((x) => (x.id === it.id ? { ...x, active: !x.active } : x));
    setItems(next);
    try { await api.put(`/admin/items/${it.id}`, { ...it, active: !it.active }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setItems(items); }
  };
  const deleteItem = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try {
      await api.delete(`/admin/items/${it.id}`);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reorderItems = async (sectionId, ids) => {
    // Rebuild the sorted list with fresh objects — never mutate in place.
    const orderedThisSection = ids
      .map((id, i) => {
        const src = items.find((x) => x.id === id);
        return src ? { ...src, order: i } : null;
      })
      .filter(Boolean);
    const others = items.filter((x) => x.section_id !== sectionId);
    setItems([...others, ...orderedThisSection]);
    try {
      await api.post("/admin/items/reorder", ids);
      toast.success("Order updated", { duration: 1500 });
    } catch (e) {
      toast.error("Couldn't reorder items — " + formatApiErrorDetail(e.response?.data?.detail));
      load();
    }
  };

  // ------------------------------------------------ Cross-section drag handling
  // The DndContext at this level receives every drop across every section.
  // - If the item is dropped over another item in the SAME section → reorder
  // - If over an item in a DIFFERENT section → move + reorder into that section
  // - If over a section's empty/append dropzone → append to that section
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [dragging, setDragging] = useState(null); // { id, sectionId } — for DragOverlay

  const moveItemToSection = async (itemId, targetSectionId, insertIndex) => {
    const item = items.find((x) => x.id === itemId);
    if (!item) return;
    const sourceSectionId = item.section_id;
    if (sourceSectionId === targetSectionId) return;

    // Build the target list (existing target items, minus the moved one, plus
    // the moved item at the insert position). Assign fresh sequential order.
    const otherTargetItems = items
      .filter((x) => x.section_id === targetSectionId && x.id !== itemId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = insertIndex == null || insertIndex < 0 ? otherTargetItems.length : Math.min(insertIndex, otherTargetItems.length);
    const newTargetOrder = [...otherTargetItems.slice(0, idx), { ...item, section_id: targetSectionId }, ...otherTargetItems.slice(idx)];
    const targetWithOrder = newTargetOrder.map((it, i) => ({ ...it, order: i }));

    // Source list: same items minus the moved one, re-sequenced.
    const sourceWithOrder = items
      .filter((x) => x.section_id === sourceSectionId && x.id !== itemId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((it, i) => ({ ...it, order: i }));

    // Everything else stays as-is.
    const untouched = items.filter(
      (x) => x.section_id !== targetSectionId && x.section_id !== sourceSectionId
    );

    // Write the fully-recomputed state so optimistic UI == server truth.
    setItems([...untouched, ...sourceWithOrder, ...targetWithOrder]);

    try {
      await api.put(`/admin/items/${itemId}`, {
        section_id: targetSectionId,
        name: item.name, brand: item.brand || "", description: item.description || "",
        image_url: item.image_url || "", url: item.url || "", highlight: item.highlight || "",
        price_or_meta: item.price_or_meta || "", cta_label: item.cta_label || "Learn more",
        meta_extras: item.meta_extras || [], category: item.category || "",
        active: item.active !== false,
      });
      await api.post("/admin/items/reorder", targetWithOrder.map((x) => x.id));
      if (sourceWithOrder.length) await api.post("/admin/items/reorder", sourceWithOrder.map((x) => x.id));
      toast.success("Moved to " + (tiles.find((t) => t.id === targetSectionId)?.title || targetSectionId));
    } catch (e) {
      toast.error("Couldn't move item — " + formatApiErrorDetail(e.response?.data?.detail));
      load(); // roll back to server truth on failure
    }
  };

  const onDragStart = (e) => {
    const it = items.find((x) => x.id === e.active.id);
    if (it) setDragging({ id: it.id, name: it.name, sectionId: it.section_id });
  };
  const finishDrag = () => setDragging(null);
  const onDragCancel = () => finishDrag();
  const onDragEnd = (e) => {
    finishDrag();
    const { active, over } = e;
    if (!over) return;
    // Silent same-position drops (e.g. tiny mouse jitter) → give a subtle nudge
    // so the user doesn't wonder if their drag registered at all.
    if (active.id === over.id) {
      toast("Dropped in the same spot — no change", { icon: "↩︎", duration: 1500 });
      return;
    }
    const activeSection = active.data?.current?.sectionId;
    // `over` can be either another item or a section-level dropzone.
    const overType = over.data?.current?.type;
    const overSection = over.data?.current?.sectionId;
    if (!activeSection || !overSection) {
      // iteration_17 MEDIUM: don't silently ignore an unresolved drop.
      toast("Couldn't tell where to drop that — try again slower", { icon: "↩︎" });
      return;
    }

    if (activeSection === overSection && overType === "item") {
      // Same-section reorder — existing behaviour.
      const sectionItems = itemsBySection[activeSection] || [];
      const ids = sectionItems.map((i) => i.id);
      const from = ids.indexOf(active.id);
      const to = ids.indexOf(over.id);
      if (from < 0 || to < 0 || from === to) return;
      reorderItems(activeSection, arrayMove(ids, from, to));
    } else if (activeSection !== overSection) {
      // Cross-section move. If we dropped on an item, insert just before it.
      let insertIndex;
      if (overType === "item") {
        const targetList = (itemsBySection[overSection] || []).filter((x) => x.id !== active.id);
        insertIndex = targetList.findIndex((x) => x.id === over.id);
      }
      moveItemToSection(active.id, overSection, insertIndex);
    }
  };

  const onSaved = (saved) => {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.id === saved.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [...prev, saved];
    });
  };

  if (!tiles) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-black/40" /></div>;

  const draggingItem = dragging ? items.find((x) => x.id === dragging.id) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Content</h2>
          <p className="text-xs text-black/50">Add sections and fill them with items (cards, products, services, links — any type). Drag items between sections using the ⋮⋮ handle. Rename, hide, reorder, and delete freely.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:justify-end">
          {dirty && (
            <span
              data-testid="unified-dirty-indicator"
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-800"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
              Unsaved
            </span>
          )}
          <div className="relative">
            <button
              onClick={() => setTemplatesOpen((v) => !v)}
              data-testid="unified-add-section"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-black px-4 py-2 text-xs font-semibold text-white"
            >
              <Plus size={14} /> Add section
            </button>
            {templatesOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setTemplatesOpen(false)} />
                <div
                  data-testid="unified-templates-menu"
                  className="absolute right-0 top-full z-20 mt-1 w-64 overflow-hidden rounded-xl border border-black/10 bg-white shadow-lg"
                >
                  <p className="border-b border-black/5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-black/50">Add a section</p>
                  {SECTION_TEMPLATES.map((t, i) => (
                    <button
                      key={t.key}
                      onClick={() => { setTemplatesOpen(false); if (t.key === "blank") addCustom(); else addFromTemplate(t); }}
                      data-testid={`unified-template-${t.key}`}
                      className={`flex w-full flex-col items-start gap-0.5 border-b border-black/5 px-3 py-2.5 text-left text-[12px] hover:bg-black/[0.03] ${i === 0 ? "bg-[color:#B84A26]/5" : ""}`}
                    >
                      <span className="flex w-full items-center gap-2 font-semibold">
                        {t.label}
                        {i === 0 && <span className="ml-auto rounded-full bg-[color:#B84A26]/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[color:#B84A26]">Quick</span>}
                      </span>
                      <span className="text-[10px] text-black/50">{t.hint}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button onClick={saveTiles} disabled={savingTiles || !dirty} data-testid="unified-save-sections" className="inline-flex items-center gap-1.5 self-start whitespace-nowrap rounded-full border border-black/15 px-4 py-2 text-xs font-semibold disabled:opacity-40 sm:self-auto">
            {savingTiles ? <Loader2 size={12} className="animate-spin" /> : null} Save sections
          </button>
        </div>
      </div>
      <div className="space-y-3">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          {tiles.map((t, i) => (
            <SectionRow
              key={t.id}
              tile={t}
              index={i}
              total={tiles.length}
              items={itemsBySection[t.id] || []}
              expanded={expanded[t.id] ?? false}
              setExpanded={(v) => setExpanded((p) => ({ ...p, [t.id]: v }))}
              onMove={moveTile}
              onEdit={(next) => updateTile(t.id, next)}
              onDelete={() => deleteTile(t.id)}
              onAddItem={() => addItem(t.id, t.title)}
              onEditItem={(it) => editItem(it, t.title)}
              onDeleteItem={deleteItem}
              onToggleItem={toggleItem}
            />
          ))}
          {/* Floating drag preview so the user can see what they're moving even
              across collapsed sections. */}
          <DragOverlay dropAnimation={null}>
            {draggingItem ? (
              <div className="flex max-w-md items-center gap-3 rounded-lg border border-black/20 bg-white p-2.5 shadow-2xl">
                {draggingItem.image_url ? <img src={resolveImg(draggingItem.image_url)} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover" /> : <div className="h-10 w-10 shrink-0 rounded-md bg-black/5" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold">{draggingItem.name}</p>
                  {draggingItem.brand ? <p className="truncate text-[10px] font-bold uppercase tracking-wider text-black/50">{draggingItem.brand}</p> : null}
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      <ItemEditor
        open={!!editing?.open}
        item={editing?.item}
        sectionTitle={editing?.sectionTitle}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />
    </div>
  );
}
